import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApiToken } from '../auth/AuthProvider';
import { listHistory, downloadReport, deleteReport, ReportMetadata } from '../services/api';
import ConfirmModal from '../components/ConfirmModal';
import LoadingAnimation from '../components/LoadingAnimation';

const styles: Record<string, React.CSSProperties> = {
  title: { fontSize: 24, fontWeight: 700, color: '#1a365d', margin: '0 0 24px' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '10px 12px', background: '#f7fafc', borderBottom: '2px solid #e2e8f0', fontSize: 13, color: '#666', fontWeight: 600 },
  td: { padding: '12px', borderBottom: '1px solid #edf2f7', fontSize: 14 },
  btn: { background: '#0078d4', color: 'white', border: 'none', padding: '6px 12px', borderRadius: 4, fontSize: 13, cursor: 'pointer', marginRight: 8 },
  btnSecondary: { background: 'white', color: '#0078d4', border: '1px solid #0078d4', padding: '6px 12px', borderRadius: 4, fontSize: 13, cursor: 'pointer' },
  empty: { color: '#999', fontSize: 15, textAlign: 'center', padding: 40 },
};

export default function HistoryPage() {
  const navigate = useNavigate();
  const getToken = useApiToken();
  const [reports, setReports] = useState<ReportMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const loadedRef = useRef(false);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    (async () => {
      try {
        const token = await getToken();
        setReports(await listHistory(token));
      } catch (e) {
        console.error('Failed to load history', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [getToken]);

  const handleDownload = async (reportId: string, format: 'pdf' | 'xlsx') => {
    try {
      const token = await getToken();
      await downloadReport(token, reportId, format);
    } catch (e) {
      alert(`Download failed: ${e}`);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      const token = await getToken();
      await deleteReport(token, confirmDelete);
      setReports((prev) => prev.filter((r) => r.report_id !== confirmDelete));
    } catch (e) {
      alert(`Delete failed: ${e}`);
    }
    setConfirmDelete(null);
  };

  if (loading) return <LoadingAnimation />;

  return (
    <div>
      <h1 style={styles.title}>Report History</h1>
      {reports.length === 0 ? (
        <p style={styles.empty}>No reports generated yet. Create your first expense report!</p>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Title</th>
              <th style={styles.th}>Date Generated</th>
              <th style={styles.th}>Period</th>
              <th style={styles.th}>Total Amount</th>
              <th style={styles.th}>Receipts</th>
              <th style={styles.th}>Download</th>
              <th style={styles.th}></th>
            </tr>
          </thead>
          <tbody>
            {reports.map((r) => (
              <tr key={r.report_id}>
                <td style={{ ...styles.td, fontWeight: 600, color: '#0078d4', cursor: 'pointer' }} onClick={() => navigate(`/history/${r.report_id}`)}>{r.title || '(untitled)'}</td>
                <td style={styles.td}>{new Date(r.generated_timestamp).toLocaleDateString()}</td>
                <td style={styles.td}>{r.date_range_start} to {r.date_range_end}</td>
                <td style={styles.td}>${r.total_amount.toFixed(2)}</td>
                <td style={styles.td}>{r.receipt_count}</td>
                <td style={styles.td}>
                  <button style={styles.btn} onClick={() => handleDownload(r.report_id, 'pdf')}>PDF</button>
                  <button style={styles.btnSecondary} onClick={() => handleDownload(r.report_id, 'xlsx')}>Excel</button>
                </td>
                <td style={styles.td}>
                  <button
                    style={{ background: 'none', border: 'none', color: '#e53e3e', cursor: 'pointer', fontSize: 13 }}
                    onClick={() => setConfirmDelete(r.report_id)}
                  >Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <ConfirmModal
        open={!!confirmDelete}
        title="Delete Report"
        message="This action cannot be undone. All receipts within the report will be returned to Uncategorized Receipts."
        confirmLabel="Delete"
        danger
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}
